/**
 * Yearly View Component
 * Shows hours logged for each month of the year
 */

import React, { useMemo } from 'react';
import { Card, Tooltip, Spin, Progress } from 'antd';
import { useMonthlySummaries } from '@/hooks/useTimesheet';
import dayjs from 'dayjs';
import './YearlyView.css';

interface YearlyViewProps {
  year: number;
  showAll: boolean;
  canEdit: boolean;
}

export const YearlyView: React.FC<YearlyViewProps> = ({
  year,
  showAll,
  canEdit: _canEdit,
}) => {
  const { data, isLoading } = useMonthlySummaries({
    year,
  });

  // Group summaries by month
  const summariesByMonth = useMemo(() => {
    const summaries = data?.items || [];
    const grouped: Record<number, typeof summaries> = {};
    summaries.forEach((summary) => {
      if (!grouped[summary.month]) {
        grouped[summary.month] = [];
      }
      grouped[summary.month].push(summary);
    });
    return grouped;
  }, [data?.items]);

  // Calculate data for each month
  const monthData = useMemo(() => {
    return Array.from({ length: 12 }, (_, i) => {
      const month = i + 1;
      const monthSummaries = summariesByMonth[month] || [];

      const hours = monthSummaries.reduce((sum, s) => {
        return sum + (showAll ? s.total_hours_logged : s.approved_hours_logged);
      }, 0);

      const billable = monthSummaries.reduce(
        (sum, s) => sum + s.billable_hours_logged,
        0
      );

      const nonBillable = monthSummaries.reduce(
        (sum, s) => sum + s.non_billable_hours_logged,
        0
      );

      const projects = monthSummaries.length;

      const monthName = dayjs().month(i).format('MMM');
      const isCurrentMonth =
        month === dayjs().month() + 1 && year === dayjs().year();

      return {
        month,
        monthName,
        hours,
        billable,
        nonBillable,
        projects,
        isCurrentMonth,
      };
    });
  }, [summariesByMonth, showAll, year]);

  // Calculate totals
  const totals = monthData.reduce(
    (acc, month) => ({
      hours: acc.hours + month.hours,
      billable: acc.billable + month.billable,
      nonBillable: acc.nonBillable + month.nonBillable,
    }),
    { hours: 0, billable: 0, nonBillable: 0 }
  );

  // Find max hours for scaling
  const maxHours = Math.max(...monthData.map((m) => m.hours), 1);

  if (isLoading) {
    return (
      <div className="timesheet-yearly-loading">
        <Spin size="large" />
      </div>
    );
  }

  return (
    <div className="timesheet-yearly-view">
      {/* Header */}
      <div className="timesheet-yearly-header">
        <h3 className="timesheet-yearly-title">{year} Overview</h3>
        <div className="timesheet-yearly-stats">
          <div className="timesheet-yearly-stat">
            <span className="timesheet-yearly-stat-label">Total Hours</span>
            <span className="timesheet-yearly-stat-value">
              {totals.hours.toFixed(0)}h
            </span>
          </div>
          <div className="timesheet-yearly-stat">
            <span className="timesheet-yearly-stat-label">Avg per Month</span>
            <span className="timesheet-yearly-stat-value">
              {(totals.hours / 12).toFixed(0)}h
            </span>
          </div>
          <div className="timesheet-yearly-stat">
            <span
              className="timesheet-yearly-stat-label"
              style={{ color: 'var(--timesheet-billable)' }}
            >
              Billable
            </span>
            <span
              className="timesheet-yearly-stat-value"
              style={{ color: 'var(--timesheet-billable)' }}
            >
              {totals.billable.toFixed(0)}h
            </span>
          </div>
          <div className="timesheet-yearly-stat">
            <span
              className="timesheet-yearly-stat-label"
              style={{ color: 'var(--timesheet-non-billable)' }}
            >
              Non-Billable
            </span>
            <span
              className="timesheet-yearly-stat-value"
              style={{ color: 'var(--timesheet-non-billable)' }}
            >
              {totals.nonBillable.toFixed(0)}h
            </span>
          </div>
        </div>
      </div>

      {/* Billable Ratio */}
      <Card className="timesheet-yearly-ratio-card">
        <div className="timesheet-yearly-ratio-header">
          <span>Billable vs Non-Billable Ratio</span>
          <span className="timesheet-yearly-ratio-percentage">
            {totals.hours > 0
              ? ((totals.billable / totals.hours) * 100).toFixed(1)
              : 0}
            % Billable
          </span>
        </div>
        <Progress
          percent={
            totals.hours > 0 ? (totals.billable / totals.hours) * 100 : 0
          }
          strokeColor="var(--timesheet-billable)"
          trailColor="var(--timesheet-non-billable)"
          showInfo={false}
          strokeWidth={20}
        />
      </Card>

      {/* Months Grid */}
      <div className="timesheet-yearly-grid">
        {monthData.map((data) => {
          const hasData = data.hours > 0;
          const percentage = maxHours > 0 ? (data.hours / maxHours) * 100 : 0;

          return (
            <Card
              key={data.month}
              className={`timesheet-yearly-card ${
                data.isCurrentMonth ? 'timesheet-yearly-card-current' : ''
              } ${hasData ? 'timesheet-yearly-card-has-data' : ''}`}
            >
              <div className="timesheet-yearly-card-header">
                <span className="timesheet-yearly-month-name">
                  {data.monthName}
                </span>
              </div>

              {hasData ? (
                <Tooltip
                  title={
                    <div>
                      <div>Total: {data.hours.toFixed(2)}h</div>
                      <div>Billable: {data.billable.toFixed(2)}h</div>
                      <div>Non-Billable: {data.nonBillable.toFixed(2)}h</div>
                      <div>Projects: {data.projects}</div>
                    </div>
                  }
                >
                  <div className="timesheet-yearly-card-content">
                    <div className="timesheet-yearly-card-hours">
                      {data.hours.toFixed(0)}
                      <span className="timesheet-yearly-card-unit">h</span>
                    </div>

                    {/* Visual Bar */}
                    <div className="timesheet-yearly-card-bar-container">
                      <div
                        className="timesheet-yearly-card-bar"
                        style={{ height: `${percentage}%` }}
                      >
                        <div
                          className="timesheet-yearly-card-bar-billable"
                          style={{
                            height: `${data.billable > 0 ? (data.billable / data.hours) * 100 : 0}%`,
                          }}
                        />
                        <div
                          className="timesheet-yearly-card-bar-nonbillable"
                          style={{
                            height: `${data.nonBillable > 0 ? (data.nonBillable / data.hours) * 100 : 0}%`,
                          }}
                        />
                      </div>
                    </div>

                    {/* Breakdown Pills */}
                    <div className="timesheet-yearly-card-breakdown">
                      {data.billable > 0 && (
                        <div className="timesheet-yearly-pill timesheet-yearly-pill-billable">
                          {data.billable.toFixed(0)}h
                        </div>
                      )}
                      {data.nonBillable > 0 && (
                        <div className="timesheet-yearly-pill timesheet-yearly-pill-nonbillable">
                          {data.nonBillable.toFixed(0)}h
                        </div>
                      )}
                    </div>

                    <div className="timesheet-yearly-card-projects">
                      {data.projects}{' '}
                      {data.projects === 1 ? 'project' : 'projects'}
                    </div>
                  </div>
                </Tooltip>
              ) : (
                <div className="timesheet-yearly-card-empty">No entries</div>
              )}
            </Card>
          );
        })}
      </div>
    </div>
  );
};
